import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, Form } from '@themesberg/react-bootstrap';
import { get, post } from '../../../services/back.service';

const ALCANCES = [
  { value: 'user', label: 'Usuario' },
  { value: 'office', label: 'Oficina' },
  { value: 'profile', label: 'Perfil' },
  { value: 'all', label: 'Todos' },
];

const URL_ALCANCE = {
  user: '/users/',
  office: '/office/',
  profile: '/profile/',
};

const etiqueta = (targetType, opcion) => {
  if (targetType === 'user') {
    return `${opcion.first_name} ${opcion.last_name} (${opcion.email})`;
  }
  return opcion.name;
};

/**
 * Formulario de envío de anuncios dirigidos a un usuario, una oficina, un
 * perfil o **todos** los usuarios internos (una fila de notificación por
 * destinatario activo, en el backend; los clientes quedan fuera por modelo).
 *
 * Control de acceso: el menú solo muestra la entrada con el módulo
 * `Enviar Anuncios`, y aquí se comprueba antes de enviar; el backend vuelve a
 * validar con 403. La respuesta informa cuántos destinatarios lo recibieron.
 */
export default function SendAnnouncementForm() {
  const menu = useSelector((state) => state.user.menu);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState('user');
  const [targetId, setTargetId] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [alerta, setAlerta] = useState({ show: false, type: '', message: '' });

  // Misma comprobación que hace el backend: el perfil debe tener el módulo.
  const tieneModulo = useMemo(() => {
    const items = Object.values(menu || {}).flat();
    return items.some((item) => item && item.url === '/sendannouncement');
  }, [menu]);

  // Opciones del selector según el alcance; al cambiar de alcance se limpia
  // la selección para no enviar un id de otra categoría. `all` no tiene
  // selector: todos los usuarios internos activos reciben el anuncio.
  useEffect(() => {
    let cancelado = false;
    setTargetId('');

    if (targetType === 'all') {
      setOpciones([]);
      return undefined;
    }

    const cargar = async () => {
      const respuesta = await get(URL_ALCANCE[targetType]);
      if (cancelado) return;
      const data = respuesta && respuesta.success ? respuesta.data : [];
      const activos = Array.isArray(data) ? data.filter((item) => item.state) : [];
      setOpciones(activos);
    };

    cargar();
    return () => {
      cancelado = true;
    };
  }, [targetType]);

  const enviar = async (event) => {
    event.preventDefault();

    if (!title.trim() || !message.trim()) {
      setAlerta({ show: true, type: 'warning', message: 'El título y el mensaje son obligatorios.' });
      return;
    }
    if (!targetId && targetType !== 'all') {
      setAlerta({ show: true, type: 'warning', message: 'Selecciona el destinatario.' });
      return;
    }
    if (!tieneModulo) {
      setAlerta({
        show: true,
        type: 'danger',
        message: 'No tienes el módulo Enviar Anuncios asignado.',
      });
      return;
    }

    const payload = {
      title: title.trim(),
      message: message.trim(),
      target_type: targetType,
    };
    if (targetType !== 'all') payload.target_id = Number(targetId);

    setEnviando(true);
    const respuesta = await post('/notify/announcements', payload);
    setEnviando(false);

    if (respuesta && respuesta.success) {
      const total = respuesta.data.recipients;
      setAlerta({
        show: true,
        type: 'success',
        message: `Anuncio enviado a ${total} ${total === 1 ? 'destinatario' : 'destinatarios'}.`,
      });
      setTitle('');
      setMessage('');
      setTargetId('');
    } else {
      setAlerta({
        show: true,
        type: 'danger',
        message: (respuesta && respuesta.message) || 'No se pudo enviar el anuncio.',
      });
    }
  };

  return (
    <Card className="p-4">
      <h4 className="mb-4">Enviar anuncio</h4>

      {alerta.show && (
        <Alert variant={alerta.type} onClose={() => setAlerta({ ...alerta, show: false })} dismissible>
          {alerta.message}
        </Alert>
      )}

      <Form onSubmit={enviar}>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="announcement-title">Título</Form.Label>
          <Form.Control
            id="announcement-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Comunicado de gerencia"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label htmlFor="announcement-message">Mensaje</Form.Label>
          <Form.Control
            id="announcement-message"
            as="textarea"
            rows={4}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Escribe el mensaje para los destinatarios"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label as="legend">Alcance</Form.Label>
          {ALCANCES.map((alcance) => (
            <Form.Check
              key={alcance.value}
              inline
              type="radio"
              id={`announcement-target-${alcance.value}`}
              name="announcement-target-type"
              label={alcance.label}
              value={alcance.value}
              checked={targetType === alcance.value}
              onChange={() => setTargetType(alcance.value)}
            />
          ))}
        </Form.Group>

        {targetType !== 'all' && (
          <Form.Group className="mb-4">
            <Form.Label htmlFor="announcement-target">Destinatario</Form.Label>
            <Form.Select
              id="announcement-target"
              value={targetId}
              onChange={(event) => setTargetId(event.target.value)}
            >
              <option value="">Selecciona...</option>
              {opciones.map((opcion) => (
                <option key={opcion.id} value={opcion.id}>
                  {etiqueta(targetType, opcion)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Button variant="primary" type="submit" disabled={enviando}>
          {enviando ? 'Enviando...' : 'Enviar anuncio'}
        </Button>
      </Form>
    </Card>
  );
}
